import { Layout } from 'react-grid-layout';
import type { ComponentData, LayoutData } from '../../api/pages-client/pages-client.type';
import type { CustomSprintMeasureDataResponse } from '../../api/work-periods-client/work-periods-client.type';

/**
 * Returns the appropriate icon name for a given widget type and subtype
 * @param type - The main widget type ('chart', 'table', 'tile', 'metric')
 * @param subType - Optional subtype for chart widgets ('line', 'area', 'bar', 'stacked-bar', 'pie')
 * @returns The icon name string to be used with the Icon component
 */
const getWidgetIcon = (type: string, subType?: string): string => {
  switch (type) {
    case 'chart':
      switch (subType) {
        case 'line':
          return 'show_chart';
        case 'area':
          return 'area_chart';
        case 'bar':
          return 'bar_chart';
        case 'stacked-bar':
          return 'stacked_bar_chart';
        case 'pie':
          return 'pie_chart';
        default:
          return 'analytics';
      }
    case 'table':
      return 'table_chart';
    case 'tile':
      return 'numbers';
    case 'metric':
      return 'speed';
    default:
      return 'dashboard';
  }
};

/**
 * Calculates the date range for a dashboard component based on its range type
 * @param {ComponentData} componentData - The component data containing the range_type
 * @returns {{ startDate: string, endDate: string }} Object containing ISO date strings for start and end dates
 *
 * @example
 * const component = { range_type: 'last_30_days' };
 * const { startDate, endDate } = getComponentDateRange(component);
 * Returns dates 30 days apart with endDate as today
 */

const getComponentDateRange = (componentData: ComponentData): { startDate: string; endDate: string } => {
  const endDate = new Date().toISOString().split('T')[0];
  let days = 30;
  switch (componentData.range_type) {
    case 'today':
      days = 0;
      break;
    case 'last_1_day':
      days = 1;
      break;
    case 'last_7_days':
      days = 7;
      break;
    case 'last_14_days':
      days = 14;
      break;
    case 'last_30_days':
      days = 30;
      break;
    case 'last_60_days':
      days = 60;
      break;
    case 'last_90_days':
      days = 90;
      break;
    case 'last_sprint':
    case 'last_2_sprints':
    case 'last_3_sprints':
    case 'last_4_sprints':
    case 'last_5_sprints':
    case 'last_6_sprints':
      days = getComponentNSprints(componentData) * 14;
      break;
  }
  const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  if (componentData.range_type === 'last_1_day') {
    return { startDate: startDate, endDate: startDate };
  }
  return { startDate, endDate };
};

/**
 * Calculates the number of sprints for a dashboard component based on its range type
 * @param {ComponentData} componentData - The component data containing the range_type
 * @returns {number} The number of sprints
 *
 * @example
 * const component = { range_type: 'last_sprint' };
 * const nSprints = getComponentNSprints(component);
 * Returns 1
 */
const getComponentNSprints = (componentData: ComponentData): number => {
  switch (componentData.range_type) {
    case 'last_sprint':
      return 1;
    case 'last_2_sprints':
      return 2;
    case 'last_3_sprints':
      return 3;
    case 'last_4_sprints':
      return 4;
    case 'last_5_sprints':
      return 5;
    case 'last_6_sprints':
      return 6;
    default:
      return 1;
  }
};

/**
 * Checks if an object is an instance of CustomMeasureDataByEntity
 * @param obj - The object to check
 * @returns {boolean} True if the object is an instance of CustomMeasureDataByEntity, false otherwise
 */
function isCustomSprintMeasureDataResponse(obj: any): obj is CustomSprintMeasureDataResponse {
  // Check if object is a CustomMeasureDataByEntity by verifying it has UUID keys
  return (
    obj &&
    typeof obj === 'object' &&
    Object.keys(obj).length > 0 &&
    Object.keys(obj).every((key) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key))
  );
}
/**
 * Creates a default 3x3 grid layout for widgets without layout data
 * @param {string} componentId - The unique identifier for the component
 * @param {number} index - The index of the component in the array
 * @returns {Layout} A default layout configuration for the component
 */
const createDefaultLayout = (componentId: string, index: number): Layout => {
  const row = Math.floor(index / 3);
  const col = index % 3;

  return {
    i: componentId,
    x: col * 4,
    y: row * 3,
    w: 4,
    h: 3,
  };
};

/**
 * Converts LayoutData object to Layout object required by react-grid-layout
 * @param {LayoutData} layoutData - The layout data stored in the database
 * @param {string} componentId - The unique identifier for the component
 * @returns {Layout} A layout configuration compatible with react-grid-layout
 */
const layoutDataToLayout = (layoutData: LayoutData, componentId: string): Layout => {
  return {
    i: componentId,
    x: layoutData.x,
    y: layoutData.y,
    w: layoutData.w,
    h: layoutData.h,
  };
};

/**
 * Converts Layout object from react-grid-layout to LayoutData for storage
 * @param {Layout} layout - The layout object from react-grid-layout
 * @returns {LayoutData} A simplified layout data object for storage
 */
const layoutToLayoutData = (layout: Layout): LayoutData => {
  return {
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
  };
};

export {
  createDefaultLayout,
  getComponentDateRange,
  getComponentNSprints,
  getWidgetIcon,
  isCustomSprintMeasureDataResponse,
  layoutDataToLayout,
  layoutToLayoutData,
};
